import React, { useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { useToast } from '../../context/ToastContext';
import {
  Settings,
  Lock,
  Save,
  Eye,
  EyeOff
} from 'lucide-react';
import './AdminSettings.css';

const AdminSettings: React.FC = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { changePassword } = useAdmin();
  const { showToast } = useToast();

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }

    if (newPassword.length < 6) {
      showToast('New password must be at least 6 characters long', 'error');
      return;
    }

    setLoading(true);
    
    try {
      const success = changePassword(oldPassword, newPassword);
      
      if (success) {
        showToast('Password changed successfully!', 'success');
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        showToast('Current password is incorrect', 'error');
      }
    } catch (error) {
      showToast('Failed to change password', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-settings">
      <div className="admin-settings-header">
        <h2 className="admin-section-title">
          <Settings size={24} />
          Admin Settings
        </h2>
      </div>

      <div className="admin-settings-content">
        <div className="admin-settings-section">
          <h3 className="admin-settings-section-title">
            <Lock size={20} />
            Change Password
          </h3>
          <p className="admin-settings-description">
            Update your admin panel password. Use a strong password to keep your account secure.
          </p>

          <form onSubmit={handlePasswordChange} className="admin-password-form">
            <div className="admin-form-group">
              <label>Current Password</label>
              <div className="admin-password-input-wrapper">
                <input
                  type={showOldPassword ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  className="admin-password-toggle"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                >
                  {showOldPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="admin-form-group">
              <label>New Password</label>
              <div className="admin-password-input-wrapper">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Enter new password"
                  minLength={6}
                />
                <button
                  type="button"
                  className="admin-password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <small className="admin-form-hint">
                Password must be at least 6 characters long
              </small>
            </div>

            <div className="admin-form-group">
              <label>Confirm New Password</label>
              <div className="admin-password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  className="admin-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="admin-form-actions">
              <button
                type="submit"
                className="admin-btn admin-btn-primary"
                disabled={loading}
              >
                <Save size={16} />
                {loading ? 'Changing Password...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>

        <div className="admin-settings-section">
          <h3 className="admin-settings-section-title">
            Admin Information
          </h3>
          <div className="admin-info-grid">
            <div className="admin-info-item">
              <label>Username</label>
              <div className="admin-info-value">admin</div>
            </div>
            <div className="admin-info-item">
              <label>Role</label>
              <div className="admin-info-value">Super Administrator</div>
            </div>
            <div className="admin-info-item">
              <label>Last Login</label>
              <div className="admin-info-value">{new Date().toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div className="admin-settings-section">
          <h3 className="admin-settings-section-title">
            System Information
          </h3>
          <div className="admin-info-grid">
            <div className="admin-info-item">
              <label>Application</label>
              <div className="admin-info-value">NaatCity Admin Panel</div>
            </div>
            <div className="admin-info-item">
              <label>Version</label>
              <div className="admin-info-value">1.0.0</div>
            </div>
            <div className="admin-info-item">
              <label>Environment</label>
              <div className="admin-info-value">Production</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;